// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }
  // return `http://localhost:4000`;

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

/**
 * Give a failed-request Error a usable `message` and `status`.
 *
 * `Object.assign(new Error(), data)` alone cannot do this: `message` is an OWN property of every
 * `new Error()` (empty string), and this template's server routes report failures as
 * `{ error, code }` — no `message` key — so the assign never overwrites it. The result was that
 * every 4xx/5xx surfaced in the browser as a bare `Error` with an empty message and only a
 * `bundle.js` frame, making server-side rejections effectively undiagnosable from the client.
 *
 * Purely additive: an existing `message` (or `status`) on the response body always wins, so
 * callers that already read `.message`/`.code`/`.status` are unaffected.
 */
const decorateApiError = (e, data, status) => {
  if (!e.message) {
    e.message = (data && (data.error || data.code)) || `Request failed with status ${status}`;
  }
  if (e.status === undefined) {
    e.status = status;
  }
  return e;
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  // A body the caller has already serialized itself — anything sent with an explicit content type,
  // such as plain JSON — is passed through untouched. It must still be passed: `body` is
  // destructured out of `options` above, so anything not restored here is dropped silently and the
  // request leaves the browser empty, which the server can only report as a missing field.
  const bodyMaybe = body ? { body: shouldSerializeBody ? serialize(body) : body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  if (headers && headers['Content-Type'] === 'application/file') {
    return fetch(url, {
      method: "POST",
      body: body,
      credentials: 'include',
    }).then(res => {
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

      if (res.status >= 400) {
        return res.json().then(data => {
          let e = new Error();
          e = Object.assign(e, data);
          throw decorateApiError(e, data, res.status);
        });
      }
      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      } else if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    });
  }
  else {
  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw decorateApiError(e, data, res.status);
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
  }
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const searchUsers = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/searchUsers?params=${params}`, {
    method: methods.GET,
  });
};

// Ask for, decide, withdraw, hide or show a referral or recommendation. Answers { entry }.
export const endorsementAction = body => post('/api/endorsements', body);

// Record a project against an agency link, or, as the agency's admin, hide or show one recorded
// against the company. Answers { project }.
export const agencyProjectAction = body => post('/api/agency-projects', body);

export const getAddedUsersOfCompany = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getAddedUsersOfCompany?params=${params}`, {
    method: methods.GET,
  });
};

export const getUserEmail = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getUserEmail?params=${params}`, {
    method: methods.GET,
  });
};

export const getOwnJobRfpListings = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getOwnJobRfpListings?params=${params}`, {
    method: methods.GET,
  });
};

export const openCloseListings = body => {
  return post('/api/openCloseListings', body);
};

export const conversations = body => post('/api/conversations', body);

export const chatMessages = body => post('/api/chatMessages', body);

export const manualTransactions = body => {
  return post('/api/manualTransactions', body);
};

export const manualFieldOptions = body => {
  return post('/api/manualFieldOptions', body);
};

export const addCustomFieldOption = ({ fieldType, option }) => {
  return manualFieldOptions({
    type: 'send',
    fieldName: fieldType,
    key: option?.key,
    label: option?.label,
  });
};

// Persist a brand-new (not-yet-onboarded) candidate an agency is applying on
// behalf of. Returns { pendingCandidateId, invitationToken }.
export const createPendingCandidate = body => {
  return post('/api/pendingCandidates', body);
};

// Poll the resume-parser state for the signed-in user. Deliberately narrow: the
// profile page polls this on a timer, so it must not pull page data or touch Redux.
export const getResumeParseStatus = () => {
  return request('/api/resumeParseStatus', { method: methods.GET });
};

// Fetch a pending candidate by id to prefill the activation onboarding form.
export const getPendingCandidate = pendingCandidateId => {
  return request(`/api/pendingCandidates?pendingCandidateId=${encodeURIComponent(pendingCandidateId)}`, {
    method: methods.GET,
  });
};

// export const getManualMessages = transactionId => {
//   return get(`/api/manualMessages?transactionId=${transactionId}`);
// };

export const saveRFPPayments = body => {
  return post('/api/saveRFPPayments', body);
};

export const saveWalletPagePayments = body => {
  return post('/api/saveWalletPagePayments', body);
};

export const saveConsent = body => {
  return post('/api/saveConsent', body);
};

export const fetchConsent = body => {
  return post('/api/fetchConsent', body);
};

export const createNewListing = body => {
  return post('/api/createNewListing', body);
};

export const duplicateListing = body => {
  return post('/api/duplicateListing', body);
};

// A job or RFP read on the server, for whoever the posting admits; answers the marketplace's shape.
export const postingShow = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/postings/show?params=${params}`, {
    method: methods.GET,
  });
};

export const getListingWithPrivateData = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getListingWithPrivateData?params=${params}`, {
    method: methods.GET,
  });
};

export const updateTransition = body => {
  return post('/api/updateTransition', body);
};

export const getUserDetails = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getUserDetails?params=${params}`, {
    method: methods.GET,
  });
};

export const createCheckoutSession = body => {
  return post('/api/createCheckoutSession', body);
};

export const createSubscriptionSetupIntent = body => {
  return post('/api/createSubscriptionSetupIntent', body);
};

export const getSubscriptionPaymentMethod = body => {
  return post('/api/getSubscriptionPaymentMethod', body);
};

export const updateSubscriptionPaymentMethod = body => {
  return post('/api/updateSubscriptionPaymentMethod', body);
};

export const deleteSubscriptionPaymentMethod = body => {
  return post('/api/deleteSubscriptionPaymentMethod', body);
};

export const getSubscriptionList = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getSubscriptionList?params=${params}`, {
    method: methods.GET,
  });
};

export const getSubscriptionPrice = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getSubscriptionPrice?params=${params}`, {
    method: methods.GET,
  });
};

// The job-slot, RFP-slot and message-pack prices the signed-in user may buy, as
// `{ jobSlot, rfpSlot, messagePack }`; each is a price or null when that kind is not offered.
export const purchasePrices = () => {
  return request('/api/purchasePrices', { method: methods.GET });
};

export const fetchSubscription = body => {
  const params = encodeURIComponent(JSON.stringify({ subscriptionId: body }));
  return request(`/api/fetchSubscription?params=${params}`, {
    method: methods.GET,
  });
};

export const updateTransactionMetadata = body => {
  return post('/api/updateTransactionMetadata', body);
};

export const trackIntegrityCheck = body => {
  return post('/api/trackIntegrityCheck', body);
};

// The documents each Integrity Check scope would ask of this candidate, for the employer choosing
// one: `{ identity: { documents, anyOf }, work: { documents | null, workAuthTypeMissing } }`, where
// `documents` lists alternatives and each alternative is a set of names held together.
export const integrityCheckRequiredDocuments = ({ candidateId, listingId }) => {
  const query = new URLSearchParams({ candidateId, listingId }).toString();
  return request(`/api/integrityCheckRequiredDocuments?${query}`, {
    method: methods.GET,
  });
};

// Asks the server to re-check a Verified Integrity Check whose documents have lapsed. `documents`,
// when given, names the lapsed documents to ask for; without it every lapsed document is asked
// for. Answers `{ uiStatus, scope, lapsedCount, creditDeducted }`, where `uiStatus` is
// `RECHECK_REQUESTED`, or `COMPLETED` / `COMPLETED_LAPSED` when the chosen documents were verified
// again meanwhile; a refusal rejects with its status and code (`RECHECK_NOT_AVAILABLE`,
// `INSUFFICIENT_INTEGRITY_CHECK_CREDITS`, `RECHECK_FAILED`, `INVALID_DOCUMENTS`).
export const requestIntegrityCheckRecheck = ({ candidateId, listingId, documents }) => {
  const documentsMaybe = Array.isArray(documents) ? { documents } : {};
  return post('/api/requestIntegrityCheckRecheck', { candidateId, listingId, ...documentsMaybe });
};

// Every document the employer's completed check covers, with its status:
// `{ documents: [{ key, name, status: 'verified' | 'expired' | 'withdrawn' | 'removed',
// askedElsewhere }], recheckOpen, askedByThisCheck }`. `key` is what a re-check request names.
export const fetchIntegrityCheckDocuments = ({ candidateId, listingId }) => {
  const query = new URLSearchParams({ candidateId, listingId }).toString();
  return request(`/api/integrityCheckDocuments?${query}`, {
    method: methods.GET,
  });
};

// The documents on the employer's Verified check that the candidate no longer holds, by name only:
// `{ documents: [{ name, state: 'expired' | 'withdrawn' }] }`.
export const fetchIntegrityCheckLapsedDocuments = ({ candidateId, listingId }) => {
  const query = new URLSearchParams({ candidateId, listingId }).toString();
  return request(`/api/integrityCheckLapsedDocuments?${query}`, {
    method: methods.GET,
  });
};

export const checkCustomerIntegrityCheckStatus = candidateListingId => {
  return request(`/api/checkCustomerIntegrityCheckStatus?candidateListingId=${encodeURIComponent(candidateListingId)}`, {
    method: 'GET',
  });
};

export const getJobApplicationMatchScore = ({ transactionId, listingId }) => {
  return request(`/api/getJobApplicationMatchScore?transactionId=${encodeURIComponent(transactionId)}&listingId=${encodeURIComponent(listingId)}`, {
    method: 'GET',
  });
};

// What each Wallet document needs before it can be verified, as the server decides it:
// `{ states: { [itemId]: { state, reason? } } }`. `scope` is 'self' or 'company'.
export const fetchWalletDocumentStates = ({ scope }) => {
  return request(`/api/walletDocumentStates?scope=${encodeURIComponent(scope)}`, { method: 'GET' });
};

// Asks the server to open (or resume) the verification of one Wallet document. Answers
// `{ inquiryId, sessionToken, itemId, documentName, resumed }`; a refusal rejects with its status and code.
export const openWalletVerification = ({ itemId, scope }) => {
  return post('/api/openWalletVerification', { itemId, scope });
};

// Asks the server to record the documents a finished Wallet checkout paid for.
export const walletVerificationGrant = ({ sessionId }) => {
  return post('/api/walletVerificationGrant', { sessionId });
};

export const fetchCandidateIntegrityStatus = () => {
  return request('/api/candidateIntegrityCheckStatus', { method: 'GET' });
};

export const markIntegrityCheckVerificationCompleted = () => {
  return post('/api/markIntegrityCheckVerificationCompleted', {});
};

export const getIntegrityCompletionNotifications = () => {
  return request('/api/getIntegrityCompletionNotifications', { method: 'GET' });
};

// --- SkillFit interview gate ---
export const fetchSkillFitInterviewGateStatus = token => {
  return request(`/api/skillFitInterviewGateStatus/${encodeURIComponent(token)}`, {
    method: 'GET',
  });
};

/**
 * The three fields that only DESCRIBE an interview, read from inside the room.
 *
 * ⭐ A separate call on purpose. Entry no longer resolves these — a slow lookup for the candidate's
 * name, the job title or the employer once held a recruiter outside an interview that ran without
 * them — so the room asks for them after its parties are already in, and nothing it answers can
 * affect whether anyone gets in.
 *
 * ⛔ Never call this on a repeating tick. It costs two live marketplace lookups for facts that do
 * not change while an interview runs, which is the load whose slowness caused the lock-out.
 */
export const fetchSkillFitInterviewRoomDisplay = token => {
  return request(`/api/skillFitInterviewRoomDisplay/${encodeURIComponent(token)}`, {
    method: 'GET',
  });
};

// --- SkillFit interview handoff ---
// Two server hops behind one client call: GET /api/skillfitInterviewHandoff mints a single-use
// handoff token and 302s to /api/ai-interview/join, which fetch follows automatically. The handoff
// token therefore never reaches client code, and the promise resolves with the join response:
// { interviewId, roomName, livekitToken, livekitUrl, role }. Role is claimed server-side from the
// session; `livekitUrl` rides along so the caller has both halves of
// `Room.connect(url, token)` without a second, drift-prone build-time copy of the URL.
export const joinSkillFitInterviewRoom = token => {
  return request(`/api/skillfitInterviewHandoff?token=${encodeURIComponent(token)}`, {
    method: 'GET',
  });
};

// --- SkillFit interview rejoin after a dropped connection (Screen 8, interview paused) ---
// Resolves { status, reconnectToken } — the session flipped back to `in_progress`, plus a freshly
// rotated credential that MUST replace the one just spent: the server invalidates the previous
// hash on use, so a retry with the old credential can only 401.
//
// ⛔ Deliberately NOT authenticated by the app session. The route is mounted without `requireAuth`
// because the session-scoped reconnect credential IS the authentication — which is what lets a
// candidate whose cookie/session state is unavailable mid-drop still resume their own interview,
// and nobody else's.
export const resumeSkillFitInterview = ({ interviewId, token }) => {
  return post(`/api/ai-interview/sessions/${encodeURIComponent(interviewId)}/reconnect`, { token });
};

// --- SkillFit interview observer entry ---
// The observer sibling of joinSkillFitInterviewRoom, and the proctor's only door: two server hops
// behind one client call. GET /api/skillfitInterviewProctorHandoff authorizes the caller against
// entry.humanProctorUserId, mints a single-use { interviewToken, role: 'observer' } handoff token
// and 302s to /api/ai-interview/join, which fetch follows. The handoff token never reaches client
// code. Safe to repeat on an interval: hop 1 mints a fresh token each call, so the single-use
// constraint binds a token the client never holds.
// A proctor arriving before the candidate gets a 200, not an error — interviewId, roomName,
// livekitToken and livekitUrl are all null with status 'not_started'. That is the wait state,
// never a refusal.
export const joinSkillFitInterviewRoomAsObserver = token => {
  return request(`/api/skillfitInterviewProctorHandoff?token=${encodeURIComponent(token)}`, {
    method: 'GET',
  });
};

// --- SkillFit observer readiness poll ---
// Resolves { status } — the waiting screen's 5-second tick. ONE hop, no 302 to follow, no
// credential minted: the server answers from a read-only session lookup.
// ⛔ Deliberately NOT joinSkillFitInterviewRoomAsObserver on a timer. That call's second hop
// re-derives the candidate name, job title and employer through two live Sharetribe Integration
// API calls, against a marketplace-wide quota — four fields that cannot change while the proctor
// waits — and mints a LiveKit participant token discarded on every tick but the last. A proctor
// arriving 10 minutes early would spend ~240 Integration calls from one idle screen. The screen
// therefore resolves the full entry once on mount, polls this, and joins once on advance.
// A terminal session ('completed' / 'failed') arrives here as a 200 body, never as an error —
// the poll's stop condition is a status to read, not an exception to catch.
export const fetchSkillFitObserverReadiness = token => {
  return request(`/api/skillfitInterviewProctorReadiness?token=${encodeURIComponent(token)}`, {
    method: 'GET',
  });
};

// --- Start Interview (the explicit control that BEGINS the interview) ---
// Body is `{ interviewToken }` and NOTHING else — the same no-interviewId contract as
// `endSkillFitInterview`, for a related reason: Start dispatches the AI interviewer and opens the
// STT/TTS/LLM stack for the interview's full length, so the server never acts on an id taken from
// the request. It resolves ownership from the token (honoured only because it independently found
// it among this candidate's own invitations) and reads the interviewId out of the session that
// token owns.
//
// Resolves `{ success, started, status }`. ⚠ `started: false` with `status: 'in_progress'` is the
// IDEMPOTENT case, NOT a failure — either party may press Start, so two presses racing
// is ordinary. Callers must treat any 200 as "the interview is running".
// 409 `SESSION_NOT_IN_ROOM` is the one genuine misuse: Start pressed on a session nobody has
// entered, or one already over.
export const startSkillFitInterview = body => {
  return post('/api/ai-interview/start-interview', body);
};

// --- The interview room's own read-only session poll ---
// Body is `{ interviewToken, role }`. Resolves `{ status, interviewerDispatchedAt }`.
//
// WHY IT EXISTS. The room learns everything from its join answer, which is a SNAPSHOT nothing
// re-fetches — so a Start pressed by one party left the OTHER party's pre-interview banner up
// forever, and an interviewer that never arrived was never noticed at all.
//
// ⛔ It is NOT a re-poll of either waiting-screen endpoint, and the reason is not convenience: the
// observer's readiness poll 403s every candidate, the candidate's hold poll is candidate-only, and
// BOTH of them WRITE PRESENCE. Presence means "waiting on my waiting screen" — it gates a
// candidate's entry and starts their wait clock — so a party who is already in the room ticking
// through either would register as waiting and corrupt both. This call writes nothing at all.
//
// POST rather than GET for the same reason the hold poll is a POST: ownership is re-derived
// server-side, and the body's `interviewToken` is only a disambiguator for a candidate holding
// several invitations — which a query param cannot supply to the shared resolver.
//
// ⚠ `role` is a HINT and nothing more. It only reorders the two authorization arms so an observer's
// tick does not log a candidate-resolver denial every five seconds; it grants no access, and a wrong
// hint costs one reordered attempt.
//
// ⚠ `interviewerDispatchedAt` is the moment a dispatch was ATTEMPTED. ⛔ It is NEVER evidence that
// an interviewer arrived — it is stamped even where the dispatch could not happen, which is exactly
// why a room that looked healthy sat in silence. Arrival is the room's participant list, only.
export const fetchSkillFitSessionStatus = ({ interviewToken, role } = {}) => {
  return post('/api/ai-interview/session-status', { interviewToken, role });
};

// --- The interview room asks for another interviewer ---
// Body is `{ interviewToken, role }`, the same ownership contract as the poll above. Resolves
// `{ redispatched, reason }`.
//
// ⚠ `redispatched: false` is an ANSWER, not a failure: "an interviewer is already there" and "your
// attempt is still arriving" are both 200s and both good news for whoever pressed. The server holds
// every guard that keeps a retry from putting a SECOND interviewer into the room — two of them talk
// over each other into one transcript that is then graded as the candidate's answers.
export const retrySkillFitInterviewer = ({ interviewToken, role } = {}) => {
  return post('/api/ai-interview/retry-interviewer', { interviewToken, role });
};

// --- The held candidate's poll — "is my interviewer here yet?" ---
// The candidate-side mirror of `fetchSkillFitObserverReadiness`, and the hold screen's 5-second
// tick. Resolves `{ held, holdReason, status, proctorPresent, holdExpiresAt, holdExpired }` — the
// same four hold fields the join response carries, so the screen reads ONE shape from both calls.
//
// ⛔ The hold screen must NEVER re-poll `joinSkillFitInterviewRoom` instead. That call's handoff
// token is single-use, so a polling hold screen would burn a fresh redemption every tick and pay
// its second hop's two live Integration API calls with it. Join once, poll this, join once more
// when `held` goes false.
//
// POST where the proctor's twin is a GET: ownership is re-derived server-side from the
// authenticated user, and the body's `interviewToken` is only a disambiguator for a candidate
// holding several invitations — which a query param cannot supply to the shared resolver.
// ⚠ A held candidate is a WAIT, not a failure: every answer about the hold is a 200 this
// screen reads. 403 (not your interview) is the only refusal.
export const fetchSkillFitCandidateHold = body => {
  return post('/api/ai-interview/candidate-hold', body);
};

// --- Departure beacons: the other end of the two waiting-screen polls ---
//
// Presence is sticky server-side: a party stays present until they say they left, which is what
// stops a covered or throttled waiting screen from reading as "gone" and holding a candidate out of
// a room whose observer is sitting right in front of it. The price of that is that a closing window
// has to say so, and these two helpers are where it says it.
//
// ⚠ Best-effort by construction: one attempt, no retry, no response to read, and nothing at all
// from a crash, a force-quit or a closed lid. The server's unreported-departure backstop is what
// bounds that case — ⛔ never treat a beacon as the only way a party stops counting as present.
//
// `sendBeacon` over a plain `fetch` because a request started in a `pagehide` handler is cancelled
// with the document that started it; `keepalive` is the same guarantee for the fallback path where
// `sendBeacon` is missing.
//
// @returns {boolean} whether a delivery was ATTEMPTED — ⛔ never whether it arrived, which no
//   sender of a beacon can know. ⛔ Never throws and never returns a promise: the caller is an
//   unload handler, where a rejection has nobody left to catch it.
const sendDepartureBeacon = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // A typed Blob is how a beacon sets its Content-Type; ⚠ `application/json` is not a
      // CORS-safelisted type, so a body makes this a preflighted request. Same-origin in
      // production, where the app and this API share a host, so no preflight is reached; the
      // split-port dev server preflights it exactly as it already does for every other POST here.
      const payload = body ? new Blob([JSON.stringify(body)], { type: 'application/json' }) : null;
      return navigator.sendBeacon(url, payload) === true;
    }

    const bodyMaybe = body
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      : {};
    // Swallowed deliberately: this rejects while the tab is closing, where an unhandled rejection
    // is noise in a console nobody will ever open again.
    window
      .fetch(url, { method: 'POST', credentials: 'include', keepalive: true, ...bodyMaybe })
      .catch(() => {});
    return true;
  } catch (e) {
    return false;
  }
};

// The interview observer's waiting screen closing — the counterpart to the heartbeat that its
// readiness poll writes on every tick. Token on the query string, matching the poll and keeping a
// bodiless beacon a simple POST. See `server/api/skillfitInterviewProctorDeparted.js`.
export const reportSkillFitProctorDeparture = token => {
  if (!token) return false;
  return sendDepartureBeacon(
    `/api/skillfitInterviewProctorDeparted?token=${encodeURIComponent(token)}`
  );
};

// The held candidate's waiting screen closing. Body, not query string: the shared candidate
// resolver reads its disambiguator from the body, and a candidate holding more than one invitation
// is refused without it. The token is a disambiguator only — the server re-derives ownership from
// the authenticated session and accepts this claim solely because it independently found it among
// this candidate's own invitations.
export const reportSkillFitCandidateDeparture = interviewToken => {
  if (!interviewToken) return false;
  return sendDepartureBeacon('/api/ai-interview/candidate-hold/departed', { interviewToken });
};

// --- Identity-verification gate (challenge/capture) ---
// The server re-derives + authorizes the caller's own interviewToken from the session; the body's
// token is only accepted after it matches. Frame bytes travel over the app's existing TLS channel.
export const startIdentityVerificationChallenge = body => {
  return post('/api/ai-interview/identity/challenge', body);
};

export const submitIdentityVerificationCapture = body => {
  return post('/api/ai-interview/identity/capture', body);
};

// --- Proctoring signals (shared ingestion endpoint) ---
// The three client-side proctoring hooks (CV pipeline, browser signals, audio-timing) all POST
// through this one wrapper. Only derived boolean/enum outcomes travel to the server — never raw
// webcam frames or audio samples. The server re-derives session ownership from the authenticated
// session and computes severity itself; the client-supplied interviewId is authorized, not trusted.
export const submitProctoringEvent = body => {
  return post('/api/ai-interview/proctoring/events', body);
};

// Completes an episodic proctoring event when its episode ends, correlated by the client-generated
// `episodeId` the opening submission carried — a narrow completion write on the SAME document, never
// a second event. Body: { interviewId, interviewToken?, episodeId, episodeEndedAtMs }.
//
// `durationMs` is deliberately NOT sent: the server derives it from the persisted `startedAt` and
// this end instant, the same posture that already keeps `severity` server-computed. A close is a
// sibling of the wrapper above, not a change to it — the browser-signals and audio-timing hooks
// share `submitProctoringEvent` and are untouched by episodes.
export const closeProctoringEpisode = body => {
  return post('/api/ai-interview/proctoring/events/close', body);
};

// Submits the CV pipeline's session-level signal-observability aggregate. Body:
// { interviewId, aggregate }. A third sibling of the two wrappers above, never a change to either —
// `submitProctoringEvent` is shared with the browser-signals and audio-timing hooks and a shape change
// there would break both.
//
// The aggregate is CUMULATIVE session totals plus the full bucket list from session start, so the
// server replaces the stored row wholesale and a duplicated, retried or out-of-order flush cannot
// double-count anything. Only counts, bucket boundaries, magnitudes and declared input labels travel —
// never a webcam frame, a landmark, a transformation matrix or a per-frame sample series.
//
// The client reports what it OBSERVED and asserts no judgement: no band, percentage, window or
// completeness flag is sent, because the server classifies the aggregate and would ignore one anyway.
// The client-supplied interviewId is authorized against the caller's own session, not trusted.
export const submitProctoringSignalQuality = body => {
  return post('/api/ai-interview/proctoring/signal-quality', body);
};

// --- Coding-assessment sandbox (submit / retry) ---
// The candidate's code panel POSTs a submission through this wrapper. Only the submitted code + the
// question/session identifiers travel to the server — the server re-derives session ownership from
// the authenticated session and validates the language against the question's assigned language; a
// client-supplied interviewId is authorized, not trusted. Responses carry aggregate pass/fail counts
// only — never the hidden test set's content.
export const submitCodingAssessment = body => {
  return post('/api/ai-interview/coding/submit', body);
};

// Re-run the identical prior submission in a fresh sandbox after an infrastructure failure. The
// server rejects a retry whose submission/interviewId is outside the caller's own session (403).
export const retryCodingSubmission = (submissionId, body = {}) => {
  return post(`/api/ai-interview/coding/submissions/${encodeURIComponent(submissionId)}/retry`, body);
};

// --- Continuous identity/voice/device-integrity (shared ingestion endpoint) ---
// The four client-side continuous-identity hooks (video re-verify, voice consistency, device
// integrity, one-time voice-print baseline) POST through these two wrappers. Only derived
// outcomes/short media samples travel to the server — never a persisted embedding. The server
// re-derives session ownership from the authenticated session; a client-supplied interviewId is
// authorized, not trusted. A sustained mismatch is flag-only and never pauses the interview.
export const captureVoicePrintBaseline = body => {
  return post('/api/ai-interview/identity-integrity/voiceprint-baseline', body);
};

export const submitContinuousIdentitySample = body => {
  return post('/api/ai-interview/identity-integrity/sample', body);
};

// --- Screen-share/fullscreen gate (disclosure / grant / re-check ingestion) ---
// The server owns the disclosure state machine, the retry/attempt counters, and the grace-window's
// authoritative expiry — the client only reports which UI event/outcome occurred. The server
// re-derives the caller's own interviewId/session ownership; a client-asserted interviewId is
// authorized, not trusted, and a spoofed pause request is rejected server-side.
export const submitScreenShareDisclosureEvent = body => {
  return post('/api/ai-interview/screen-share/disclosure-event', body);
};

export const submitScreenShareGrantResult = body => {
  return post('/api/ai-interview/screen-share/grant-result', body);
};

export const submitScreenShareRecheckResult = body => {
  return post('/api/ai-interview/screen-share/recheck-result', body);
};

// Mobile-only proctor-mode scan ingestion.
// The client posts only the derived scan `result` enum + a client timestamp — never raw frame
// bytes. The server re-derives the caller's own interviewId/session ownership; a client-asserted
// interviewId is authorized, not trusted, and a foreign-session submission is rejected server-side.
export const submitMobileDeskScan = body => {
  return post('/api/ai-interview/mobile-proctor/desk-scan', body);
};

export const submitMobileRearCameraSample = body => {
  return post('/api/ai-interview/mobile-proctor/rear-camera-sample', body);
};

export const submitMobileRotationScan = body => {
  return post('/api/ai-interview/mobile-proctor/rotation-scan', body);
};

// --- End Interview (the candidate closes their own interview) ---
// Body is `{ interviewToken }` and NOTHING else — deliberately no interviewId. Ending is the one
// irreversible action in the product, so the server never acts on an id taken from the request: it
// resolves ownership from the token (honoured only because it independently found it among this
// candidate's own invitations) and reads the interviewId out of the session that token owns. The
// token is still required, because a candidate holding more than one invitation is ambiguous
// without it. 200 is idempotent — an already-terminal session is a success for the caller.
export const endSkillFitInterview = body => {
  return post('/api/ai-interview/end-interview', body);
};

export const markIntegrityCompletionNotificationSeen = body => {
  return post('/api/markIntegrityCompletionNotificationSeen', body);
};

export const updateOtherUsers = body => {
  return post('/api/updateOtherUsers', body);
};

export const updatePersonaEvents = body => {
  return post('/api/updatePersonaEvents', body);
};

export const sendVerificationStatusEmail = body => {
  return post('/api/sendVerificationStatusEmail', body);
};

export const savePersonaFieldValues = body => {
  return post('/api/savePersonaFieldValues', body);
};

export const saveKybFieldValues = body => {
  return post('/api/saveKybFieldValues', body);
};

export const getPersonaFieldValues = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return request(`/api/getPersonaFieldValues${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
  });
};

export const getCredentialVaultDocNames = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return request(`/api/getCredentialVaultDocNames${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
  });
};

// export const getPersonaEvent = body => {
//   return post('/api/getPersonaEvent', body);
// };

export const uploadFileToBlob = body => {

  return post('/api/uploadFileToBlob', body, {
    headers: {
      "Content-Type": "application/file"
    }
  });
}

export const uploadResumeToBlob = body => {
  return post('/api/uploadResumeToBlob', body, {
    headers: {
      "Content-Type": "application/file"
    }
  });
}

export const sendInviteAndUpdate = body => {
  return post('/api/sendInviteAndUpdate', body);
};

// The pending team invitations of the signed-in admin's company. Anyone else is answered 403.
export const fetchCompanyInvitations = () => {
  return request('/api/company-invitations', { method: methods.GET });
};

export const sendConsultantInvite = body => {
  return post('/api/sendConsultantInvite', body);
};

export const sendVendorInvite = body => {
  return post('/api/sendVendorInvite', body);
};

export const sendPostingInvitation = body => {
  return post('/api/sendPostingInvitation', body);
}

// Decrypts an invite/share link token server-side — the AES secret used to
// encrypt these tokens must never be shipped to the client bundle.
export const decryptInviteToken = token => {
  return post('/api/decryptInviteToken', { token });
};

export const sendConsCurrEmpMail = body => {
  return post('/api/sendConsCurrEmpMail', body);
}

export const sendMeetingScheduledMail = body => {
  return post('/api/sendMeetingScheduledMail', body);
}

export const fetchBlogs = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-blogs?params=${params}`, {
    method: methods.GET,
  });
};

export const fetchFAQS = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-faqs?params=${params}`, {
    method: methods.GET,
  });
};

export const fetchPrivacy = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-privacy?params=${params}`, {
    method: methods.GET,
  });
};

export const fetchTerms = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-terms?params=${params}`, {
    method: methods.GET,
  });
};

export const fetchPrivacyCcpa = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-privacy-ccpa?params=${params}`, {
    method: methods.GET,
  });
};

export const fetchServiceAddendums = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-service-addendums?params=${params}`, {
    method: methods.GET,
  });
};

export const fetchBlog = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-blog?params=${params}`, {
    method: methods.GET,
  });
};

export const fetchSections = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-sections?params=${params}`, {
    method: methods.GET,
  });
};

export const fetchLevelOnePages = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-level-one-pages?params=${params}`, {
    method: methods.GET,
  });
}

export const fetchTagsFilter = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/strapi/fetch-blogs-tags?params=${params}`, {
    method: methods.GET,
  });
};

export const getLocationDetails = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getLocationDetails?params=${params}`, {
    method: methods.GET,
  });
};

export const dockCreateCredentials = body => {
  return post('/api/dockCreateCredentials', body);
};

export const dockGetCredentials = body => {
  return post('/api/dockGetCredentials', body);
};

export const sendOtp = body => {
  return post('/api/sendOtp', body);
};

export const verifyOtp = body => {
  return post('/api/verifyOtp', body);
};

// The signed-in user's own verified phone numbers, as the server recorded them.
export const getPhoneVerifications = () => {
  return get('/api/phoneVerifications');
};

// Profile sharing API calls
export const shareProfile = body => {
  return post('/api/shareProfile', body);
};

export const requestShareOtp = body => {
  return post('/api/requestShareOtp', body);
};

export const verifyShareOtp = body => {
  return post('/api/verifyShareOtp', body);
};

// A company's beneficial owners, tax ID number, W-9 and insurance certificate, for its own members.
export const getCompanyCredentialDetails = listingId => {
  const query = new URLSearchParams({ listingId }).toString();
  return request(`/api/companyCredentialDetails?${query}`, {
    method: methods.GET,
  });
};

// A short-lived link to a company's W-9 or insurance certificate.
// body: { listingId, document: 'w9' | 'insurance' }; a share visitor also passes { shareVisit }.
// A share visit ({ token, session }) travels in request headers, never in a URL, so it cannot end
// up in browser history or an access log. Without one, a request is sent exactly as before.
const shareVisitOptions = ({ shareVisit } = {}) =>
  shareVisit
    ? {
        headers: {
          'Content-Type': 'application/transit+json',
          'X-Share-Token': shareVisit.token,
          'X-Share-Session': shareVisit.session,
        },
      }
    : {};

export const companyDocumentLink = (body, options) => {
  return post('/api/companyDocumentLink', body, shareVisitOptions(options));
};

// The current-employer claim an emailed link names. The token goes in a header, not the URL.
export const getCurrentEmployerClaim = token => {
  return request('/api/currentEmployerClaim', {
    method: methods.GET,
    headers: { 'X-Claim-Token': token },
  });
};

// The employer's answer to that claim: { token, answer: 'confirm' | 'deny' }.
export const answerCurrentEmployerClaim = body => {
  return post('/api/answerCurrentEmployerClaim', body);
};

export const getSharedProfile = (body, options) => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getSharedProfile?params=${params}`, {
    method: methods.GET,
    ...shareVisitOptions(options),
  });
};

export const requestProfileAccess = body => {
  return post('/api/requestProfileAccess', body);
};

export const getProfileAccessRequests = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getProfileAccessRequests?params=${params}`, {
    method: methods.GET,
  });
};

export const approveAccessRequest = body => {
  return post('/api/approveAccessRequest', body);
};

export const rejectAccessRequest = body => {
  return post('/api/rejectAccessRequest', body);
};

// Credential access request API calls (credentialVault-based, replaces Truvera/Dock flow)
export const requestCredentialAccess = body => {
  return post('/api/requestCredentialAccess', body);
};

export const getCredentialAccessRequests = params => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/getCredentialAccessRequests?${query}`, {
    method: methods.GET,
  });
};

export const approveCredentialAccess = body => {
  return post('/api/approveCredentialAccess', body);
};

export const rejectCredentialAccess = body => {
  return post('/api/rejectCredentialAccess', body);
};

export const resendCredentialAccessEmail = body => {
  return post('/api/resendCredentialAccessEmail', body);
};

// A directly shared credential opens only for a visit that a one-time code started; that visit is
// passed as { shareVisit } and travels in headers. A link that needs no code is asked for as before.
export const getSharedCredentialView = (params, options) => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/getSharedCredentialView?${query}`, {
    method: methods.GET,
    ...shareVisitOptions(options),
  });
};

// Sends a one-time code to where a credential link was shared.
// body: { shareId, token, channel: 'email' | 'sms' }
export const requestCredentialShareCode = body => {
  return post('/api/requestCredentialShareCode', body);
};

// Trades that code for a visit. body: { shareId, token, code }; resolves { verified, shareSession }.
export const verifyCredentialShareCode = body => {
  return post('/api/verifyCredentialShareCode', body);
};

export const getKybPersonaChecks = params => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/getKybPersonaChecks?${query}`, {
    method: methods.GET,
  });
};

export const getKycPersonaChecks = (params, options) => {
  const query = new URLSearchParams(params).toString();
  return request(`/api/getKycPersonaChecks?${query}`, {
    method: methods.GET,
    ...shareVisitOptions(options),
  });
};

export const sendSupportEmail = body => {
  return post('/api/sendSupportEmail', body);
};

export const sendWelcomeEmail = body => {
  return post('/api/sendWelcomeEmail', body);
};

export const sendTwilioMessage = body => {
  return post('/api/sendTwilioMessage', body);
};

export const getListingsByIds = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/getListingsByIds?params=${params}`, {
    method: methods.GET,
  });
};

export const requestDocPresentation = body => {
  return post('/api/requestDocPresentation', body);
};

// Tells the server a credential failed the browser's own signature check, so an operator hears of
// it. Only the ids travel, never a value. Resolves either way: a lost report must not break a page.
export const reportCredentialCheckFailed = ({ credentialId, documentType, surface }) =>
  post('/api/credential-check-failed', { credentialId, documentType, surface }).then(
    () => undefined,
    () => undefined
  );

export const shareCredential = body => {
  return post('/api/shareCredential', body);
};

// The signed-in person's own shares: credential shares and profile links, newest first.
export const listMyShares = () => request('/api/myShares', { method: methods.GET });

// Withdraw one of them, so its link stops working. `body` is { kind, id } as the list gave them.
export const withdrawShare = body => post('/api/withdrawShare', body);

export const iParleySubscriptions = body => {
  return post('/api/iParleySubscriptions', body);
};

export const messagePacks = body => {
  return post('/api/messagePacks', body);
};

export const jobRFPSlots = body => {
  return post('/api/jobRFPSlots', body);
};

// One page (25 rows, newest first) of the member's payment history across every purchase type,
// with the category, date-range and search filters applied by the server.
export const paymentHistory = body => {
  return post('/api/paymentHistory', body);
};

// A fresh link to one invoice's PDF or hosted page. The provider's links expire, so the page asks
// for one at the moment it is opened instead of keeping it.
export const paymentInvoiceLink = body => {
  return post('/api/paymentInvoiceLink', body);
};

export const integrityCheckCredits = body => {
  return post('/api/integrityCheckCredits', body);
};

// --- SkillFit Check (mirrors the Integrity Check helpers above) ---
export const skillFitCheckCredits = body => {
  return post('/api/skillFitCheckCredits', body);
};

export const sendSkillFitInvitation = body => {
  return post('/api/sendSkillFitInvitation', body);
};

export const resendSkillFitInvitation = body => {
  return post('/api/sendSkillFitInvitation', { ...body, resend: true });
};

// The job's last invitation configuration and active weight configuration, so the settings menu can
// open as the job was last configured. Org-scoped server-side; `{ hasConfiguration: false }` when the
// org has never sent a check on the job.
export const fetchSkillFitJobInvitationDefaults = jobListingId => {
  const params = new URLSearchParams({ jobListingId });
  return request(`/api/skillFitJobInvitationDefaults?${params.toString()}`, { method: 'GET' });
};

// One candidate's own invitation on a job: what it was sent with, the duration it was sent with
// (`sentDurationMinutes`, null when none is stored) and the job's active weight configuration, so
// the resend menu can show it. Naming the candidate selects their entry rather than the job's most
// recent one; `{ hasConfiguration: false }` when the org holds no entry for them on the job.
export const fetchSkillFitCheckInvitationConfiguration = (jobListingId, candidateListingId) => {
  const params = new URLSearchParams({ jobListingId, candidateListingId });
  return request(`/api/skillFitJobInvitationDefaults?${params.toString()}`, { method: 'GET' });
};

// Batch, org-scoped Total Score + proctoring-risk lookup for the candidate dashboard's two
// independent sort/filter axes. body = { listingId, candidateListingIds: string[] }.
export const skillFitScoreAndRiskForDashboard = body => {
  return post('/api/skillFitScoreAndRiskForDashboard', body);
};

// `jobListingId` scopes the status to ONE job application, the same optional 2nd
// positional as `skillFitReport` below: omitting it resolves the candidate's latest check
// regardless of job, which is what every caller did before this change.
export const checkCustomerSkillFitCheckStatus = (candidateListingId, jobListingId) => {
  const params = new URLSearchParams({ candidateListingId });
  if (jobListingId) params.set('jobListingId', jobListingId);
  return request(`/api/checkCustomerSkillFitCheckStatus?${params.toString()}`, { method: 'GET' });
};

export const markSkillFitCheckCompleted = body => {
  return post('/api/markSkillFitCheckCompleted', body);
};

// The candidate dashboard's Start/Continue action. `skillFitCheckId` is the stable join key, NOT a
// credential — the server re-derives ownership from the session and answers `{ path }` only for a
// check the caller actually owns. ⛔ The invitation list deliberately carries no `interviewToken` to
// navigate with; that is the point of this round trip.
export const skillFitInterviewEntry = skillFitCheckId => {
  return post('/api/skillFitInterviewEntry', { skillFitCheckId });
};

// The customer Dashboard's Result column, fetched as a SECOND phase once the rows have painted.
// Total Score is recomputed on every read by design, so ⛔ never cache this response — not in Redux,
// not in a module-level map. Incomplete checks answer `null`, ⛔ which must never render as a zero.
export const skillFitScoreAndRiskForChecks = skillFitCheckIds => {
  return post('/api/skillFitScoreAndRiskForChecks', { skillFitCheckIds });
};

// `jobListingId` scopes the report to ONE job application — the same candidate can hold a
// separate check on each job they applied to under the same org. Omitting it resolves the
// candidate's latest check regardless of job, which is what every caller did before this change.
export const skillFitReport = (candidateListingId, jobListingId) => {
  const params = new URLSearchParams({ candidateListingId });
  if (jobListingId) params.set('jobListingId', jobListingId);
  return request(`/api/skillFitReport?${params.toString()}`, { method: 'GET' });
};

// The transcript BEHIND that report — the same two listing ids, resolved by the same gate
// server-side. ⛔ No interview id and no report id is passed: the endpoint resolves the interview off
// the entry it just gated, so there is nothing here for a caller to name or to get wrong.
// ⚠ `jobListingId` is optional for the same reason as `skillFitReport` above, and the page
// always supplies it — both ids are in its route params.
export const skillFitTranscript = (candidateListingId, jobListingId) => {
  const params = new URLSearchParams({ candidateListingId });
  if (jobListingId) params.set('jobListingId', jobListingId);
  return request(`/api/skillFitTranscript?${params.toString()}`, { method: 'GET' });
};

// The CANDIDATE's own developmental feedback for one check they sat — ⛔ not a candidate-flavoured
// `skillFitReport`, which 403s non-customers by design and carries the whole reviewer document.
// `skillFitCheckId` is the row's join key, not a credential: the server ignores any identity in the
// request and looks for the check only within the caller's own invitations, so a foreign id simply
// does not resolve. Every display value arrives resolved — ⛔ the caller maps nothing.
export const skillFitCompetencyFeedback = skillFitCheckId => {
  const params = new URLSearchParams({ skillFitCheckId });
  return request(`/api/skillFitCompetencyFeedback?${params.toString()}`, { method: 'GET' });
};

// Resolves a short-lived, signed recording URL for a SkillFit interview segment
// (GET /skillfitInterviewRecordingAccess). Called by
// the SkillFit Report's videoLinkResolver at click time — the stored `{sessionBaseUrl}?t=&d=` pointer
// grants no access on its own. `t`/`d` (seconds) are optional; omitting both requests the full session.
export const getSkillFitRecordingAccess = ({ interviewId, t, d } = {}) => {
  const params = new URLSearchParams({ interviewId });
  if (t != null) params.set('t', String(t));
  if (d != null) params.set('d', String(d));
  return request(`/api/skillfitInterviewRecordingAccess?${params.toString()}`, { method: 'GET' });
};

// --- Reviewer Packet (Screen 10e) --------------------------------------------------------------
// Submits one of the four reviewer actions on a completed SkillFit Report — accept_and_submit /
// reject / offer_re_interview / technical_failure_incomplete.
// `rationale` is required server-side; org-ownership is enforced by the endpoint.
export const submitReviewerAction = body => {
  return post('/api/skillfitReviewerAction', body);
};

// Revokes or resends (retries) the package share of an Accept and Submit:
// { candidateListingId, jobListingId, shareId, op: 'revoke'|'resend', channel } → { share }.
// `channel` is the share's own, so the server can count an SMS resend toward the SMS limit.
export const skillfitPackageShareAction = body => {
  return post('/api/skillfitPackageShareAction', body);
};

// --- Shared SkillFit package (the recipient's page) ----------------------------------------------
// The recipient has no account: access rests on the link secret, a one-time code and the session a
// correct code returns. The secret and the session are sent only as headers, never in a URL, so
// neither lands in an access log, a Referer or the browser history. Plain JSON, not transit. A
// refusal rejects with `status` and the server's own fields (`error`, `remaining`, `limitReached`,
// `retryAfter`).
const packageHeaders = (secret, session) => {
  const headers = { 'Content-Type': 'application/json', 'x-package-key': secret || '' };
  if (session !== undefined) headers['x-package-session'] = session || '';
  return headers;
};

const packageQuery = shareId => `shareId=${encodeURIComponent(shareId || '')}`;

// → { state: 'open'|'expired'|'ended', organisationName }
export const skillfitPackageStatus = ({ shareId, secret }) =>
  request(`/api/skillfitPackage/status?${packageQuery(shareId)}`, {
    method: methods.GET,
    headers: packageHeaders(secret),
  });

// → { sentTo: { email?, phone? } }
export const skillfitPackageCode = ({ shareId, secret }) =>
  request('/api/skillfitPackage/code', {
    method: methods.POST,
    headers: packageHeaders(secret),
    body: JSON.stringify({ shareId }),
  });

// → { session }
export const skillfitPackageVerify = ({ shareId, secret, code }) =>
  request('/api/skillfitPackage/verify', {
    method: methods.POST,
    headers: packageHeaders(secret),
    body: JSON.stringify({ shareId, code }),
  });

// → the package
export const skillfitPackageRead = ({ shareId, secret, session }) =>
  request(`/api/skillfitPackage?${packageQuery(shareId)}`, {
    method: methods.GET,
    headers: packageHeaders(secret, session),
  });

// → { url, durationSeconds }. Always the whole recording: moments are seeked in the player.
export const skillfitPackageRecording = ({ shareId, secret, session }) =>
  request(`/api/skillfitPackage/recording?${packageQuery(shareId)}`, {
    method: methods.GET,
    headers: packageHeaders(secret, session),
  });

// → { url, name }
export const skillfitPackageResume = ({ shareId, secret, session }) =>
  request(`/api/skillfitPackage/resume?${packageQuery(shareId)}`, {
    method: methods.GET,
    headers: packageHeaders(secret, session),
  });

// Resolves the reviewer packet (Screen 10e) for a completed SkillFit Report — candidateContext,
// recommendedFocus, and actionHistory assembled server-side by
// GET /api/getSkillFitReviewerPacket. Mirrors getSkillFitRecordingAccess (query-string GET) rather
// than the body-based `get` helper, since the route keys off candidateListingId.
// `jobListingId` scopes the packet to one job application — see `skillFitReport` above.
export const getReviewerPacket = ({ candidateListingId, jobListingId } = {}) => {
  const params = new URLSearchParams({ candidateListingId });
  if (jobListingId) params.set('jobListingId', jobListingId);
  return request(`/api/getSkillFitReviewerPacket?${params.toString()}`, { method: 'GET' });
};

export const markSkillFitCheckFailed = body => {
  return post('/api/markSkillFitCheckFailed', body);
};

// Changes whether this candidate may see their own competency feedback, on an ALREADY-SENT check.
// Body: `{ candidateListingId, jobListingId, shared }`
// — the same `(candidate, job)` tuple `skillFitReport` above resolves on, which both employer
// surfaces already hold, so no new identifier is plumbed to either of them.
//
// ⛔ ONE wrapper for BOTH surfaces (the customer check row and the SkillFit Report). A second caller
// path is how the two controls would drift into writing different values. The server re-enforces
// customer role and org ownership regardless — this is a convenience, never the boundary.
export const setSkillFitFeedbackSharing = body => {
  return post('/api/setSkillFitFeedbackSharing', body);
};

export const requestHumanVerifiedInterview = body => {
  return post('/api/requestHumanVerifiedInterview', body);
};

// --- Human-observer override ---
// Observer reads the candidate's reference photo (photo-only; server enforces least-privilege) and
// submits a rationale-required, write-once verify/reject decision. Observer identity is derived
// server-side from the session — never sent from the client.
export const getObserverReferencePhoto = interviewId => {
  return request(`/api/ai-interview/override/reference-photo?interviewId=${encodeURIComponent(interviewId)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

export const submitOverrideDecision = body => {
  return request('/api/ai-interview/override/decision', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
};

export const checkCandidateSkillFitInvitations = () => {
  return request('/api/skillFitCheckStatusForCandidate', { method: 'GET' });
};

export const checkCustomerSkillFitInvitations = () => {
  return request('/api/skillFitCheckStatusForCustomer', { method: 'GET' });
};

// --- Accommodation Request ---
export const submitAccommodationRequest = body => {
  return post('/api/skillfitAccommodationRequest', body);
};

export const resolveAccommodationRequest = body => {
  return post('/api/resolveAccommodationRequest', body);
};

export const rfpFreeApplicationsUsed = body => {
  return post('/api/rfpFreeApplicationsUsed', body);
};

export const getProofRequest = requestId => {
  const url = `${apiBaseUrl()}/api/get-proof-request/${requestId}`;

  return window.fetch(url, {
    method: 'GET',
    credentials: 'include',
  }).then(res => {
    if (res.status >= 400) {
      return res.text().then(data => {
        let e = new Error('Failed to fetch proof request');
        e = Object.assign(e, { status: res.status, message: data });
        throw e;
      });
    }
    // Return HTML text content
    return res.text();
  });
};

export const fetchRequestRecords = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/fetchRequestRecords?params=${params}`, {
    method: methods.GET,
  });
};

export const updateLinkedConsultant = body => {
  return post('/api/updateLinkedConsultant', body);
};

const getWithParams = (path, params) =>
  request(`${path}?params=${encodeURIComponent(JSON.stringify(params))}`, { method: methods.GET });

// One page of one list of an organisation's consultant or vendor links, with the list's total.
export const fetchLinkedContactPage = params => getWithParams('/api/linkedContactPage', params);

// Whether the signed-in user has an unseen link notification, and the newest of each kind.
export const fetchLinkNotifications = () => request('/api/linkNotifications', { method: methods.GET });

// The caller's organisation's one staffing link to a consultant, or null.
export const fetchConsultantLink = params => getWithParams('/api/consultantLink', params);

// The caller's organisation's accepted consultants, newest first, optionally matching `q`.
export const fetchAcceptedConsultants = params => getWithParams('/api/acceptedConsultants', params);

// Preferred Network: one page of a side of the network, with its counts and the people's profiles.
export const fetchNetworkEntries = params => getWithParams('/api/network/entries', params);

// Preferred Network: one action on the list or on one entry.
export const networkAction = body => post('/api/network/entries/action', body);

// Preferred Network: the organisation's activity log, of one entry or of one side.
export const fetchNetworkActivity = params => getWithParams('/api/network/activity', params);

// Preferred Network: a company's messages to its network (preview, send, announce), and a
// recipient's answers to them (not relevant, their alert setting).
export const networkMessagesAction = body => post('/api/network/messages/action', body);

// The bell's notices, and marking some of them seen.
export const fetchNotices = () => request('/api/notices', { method: methods.GET });
export const markNoticesSeen = ids => post('/api/notices/seen', { ids });

// Preferred Network: the current view as a CSV file, as a Blob to save.
export const downloadNetworkEntriesCsv = params =>
  window
    .fetch(`${apiBaseUrl()}/api/network/entries.csv?params=${encodeURIComponent(JSON.stringify(params))}`, {
      method: methods.GET,
      credentials: 'include',
    })
    .then(res => {
      if (res.status >= 400) {
        return res.json().then(data => {
          throw decorateApiError(Object.assign(new Error(), data), data, res.status);
        });
      }
      return res.blob();
    });

export const updateLinkedVendor = body => {
  return post('/api/updateLinkedVendor', body);
};

export const fetchPersonaRecords = (body, options) => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/fetchPersonaRecords?params=${params}`, {
    method: methods.GET,
    ...shareVisitOptions(options),
  });
};

export const fetchPersonaRecordsBatch = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/fetchPersonaRecordsBatch?params=${params}`, {
    method: methods.GET,
  });
};

export const searchFilteredListings = body => {
  const params = encodeURIComponent(JSON.stringify(body));
  return request(`/api/searchFilteredListings?params=${params}`, {
    method: methods.GET,
  });
};

export const dockRevokeCredential = body => {
  return post('/api/dockRevokeCredential', body);
};

export const generatePreviewSasToken = body => {
  return post('/api/generatePreviewSasToken', body);
};

export const getPersonaFieldValuesForEmployer = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return request(`/api/getPersonaFieldValuesForEmployer${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
  });
};

// The status of each owner and signatory of the caller's company, for the profile form.
export const fetchCompanyOwnerStatuses = () => {
  return request('/api/companyOwnerStatuses', { method: methods.GET });
};

// Sends one owner's invitation again. The server refuses a second resend within the hour (429).
export const resendCompanyOwnerInvitation = body => {
  return post('/api/companyOwnerInvitations/resend', body);
};

// An owner's yes to adding the identity verification from a company's check to their Wallet.
// Body: `{ resultId }`. Answers 403 `consent_required` without the saved verification consent.
export const claimCompanyOwnerVerification = body => post('/api/companyOwnerVerifications/claim', body);
